import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Search, 
  Plus, 
  Download,
  Edit,
  Trash2,
  Gift,
  User,
  MessageSquare
} from 'lucide-react';
import { generateEventsPdf } from '../utils/pdfGenerator'; // Necesitarás crear esta función
import EventFormModal from './EventFormModal';
import { getApiUrl } from '../utils/apiConfig';


function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const offset = d.getTimezoneOffset();
  d.setMinutes(d.getMinutes() - offset);
  return d.toISOString().slice(0, 10);
}
const Events = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMonth, setFilterMonth] = useState('all');
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);

  const [members, setMembers] = useState([]);

  const [events, setEvents] = useState([]);

// Cargar eventos desde la base de datos
  const fetchEvents = () => {
  fetch(getApiUrl('/api/eventos'))
      .then(res => res.json())
      .then(data => setEvents(data));
  };

  const fetchMembers = () => {
  fetch(getApiUrl('/api/miembros'))
      .then(res => res.json())
      .then(data => setMembers(Array.isArray(data) ? data : []))
      .catch(() => setMembers([]));
  };

  useEffect(() => {
    fetchEvents();
    fetchMembers();
  }, []);

  const getMonthName = (monthNumber) => {
    const date = new Date();
    date.setMonth(monthNumber - 1);
    return date.toLocaleString('es-ES', { month: 'long' });
  };

  // Obtener usuario actual
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  // Buscar el miembro actual por email para obtener su id
  const miembroActual = members.find(m => m.email === currentUser.email);

  // Filtrar eventos: si es user, solo los propios
  const filteredEvents = events.filter(event => {
    let isOwnEvent = true;
    if (currentUser.role === 'user' && miembroActual) {
      isOwnEvent = event.memberId === miembroActual.id;
    }
    const matchesSearch = event.memberName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          event.notes.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterMonth === 'all' || new Date(event.eventDate).getMonth() + 1 === parseInt(filterMonth);
    return isOwnEvent && matchesSearch && matchesFilter;
  });

  // Estadísticas solo del usuario logeado si es rol 'user'
  let userEvents = events;
  if (currentUser.role === 'user' && miembroActual) {
    userEvents = events.filter(e => e.memberId === miembroActual.id);
  }
  const upcomingEventsCount = userEvents.filter(event => new Date(event.eventDate) >= new Date()).length;

  const handleExportPdf = () => {
    generateEventsPdf(filteredEvents);
  };

  const handleAddEvent = () => {
    setEventToEdit(null);
    setShowEventModal(true);
  };

  const handleEditEvent = (event) => {
    setEventToEdit({ ...event, eventDate: formatDate(event.eventDate) });
    setShowEventModal(true);
  };

  const handleDeleteEvent = (id) => {
    if (window.confirm('¿Está seguro de que desea eliminar este evento?')) {
  fetch(getApiUrl(`/api/eventos/${id}`), {
        method: 'DELETE'
      })
        .then(res => res.json())
        .then(() => fetchEvents());
    }
  };

 // Guardar (crear o editar) evento
  const handleSaveEvent = (newEventData) => {
    const member = members.find(m => m.id === parseInt(newEventData.memberId));
    const eventWithMemberName = { ...newEventData, memberName: member ? member.name : 'Desconocido' };

  fetch(getApiUrl('/api/eventos'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventWithMemberName)
    })
      .then(res => res.json())
      .then(() => {
        setShowEventModal(false);
        fetchEvents();
      });
  };

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
            Gestión de Eventos
          </h1>
          <p className="text-gray-600">Administre los eventos importantes de sus socios</p>
        </div>
        
        {currentUser.role !== 'user' && (
          <motion.button
            onClick={handleAddEvent}
            className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus className="w-5 h-5" />
            Registrar Evento
          </motion.button>
        )}
      </motion.div>

      {/* Event Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          { 
            title: 'Total de Eventos', 
            value: userEvents.length, 
            icon: Calendar, 
            color: 'from-blue-500 to-cyan-500',
            bgColor: 'from-blue-50 to-cyan-50'
          },
          { 
            title: 'Próximos Eventos', 
            value: upcomingEventsCount, 
            icon: Gift, 
            color: 'from-pink-500 to-red-500',
            bgColor: 'from-pink-50 to-red-50'
          }
        ].map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              className={`bg-gradient-to-br ${stat.bgColor} border border-gray-200/50 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 bg-gradient-to-br ${stat.color} rounded-xl`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
              <p className="text-gray-600 font-medium">{stat.title}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Filters and Search */}
      <motion.div
        className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-6 shadow-xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre de socio o notas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
            />
          </div>
          
          <div className="flex gap-3">
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
            >
              <option value="all">Todos los meses</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                <option key={month} value={month}>
                  {getMonthName(month)}
                </option>
              ))}
            </select>
            
            <motion.button
              onClick={handleExportPdf}
              className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors duration-200 flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Download className="w-5 h-5" />
              Exportar PDF
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Events Table */}
      <motion.div
        className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Socio</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Tipo de Evento</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Fecha del Evento</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Notas</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredEvents.map((event, index) => (
                <motion.tr
                  key={event.id}
                  className="hover:bg-gray-50/50 transition-colors duration-200"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.05 }}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">
                          {event.memberName.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{event.memberName}</p>
                        <p className="text-sm text-gray-500">Socio ID: {event.memberId}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold">
                      {event.eventType === 'birthday' ? 'Cumpleaños' : event.eventType}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(event.eventDate)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                    {event.notes || '-'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {currentUser.role !== 'user' && (
                        <>
                          <motion.button
                            onClick={() => handleEditEvent(event)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <Edit className="w-4 h-4" />
                          </motion.button>
                          <motion.button
                            onClick={() => handleDeleteEvent(event.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </motion.button>
                        </>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      <EventFormModal
        isOpen={showEventModal}
        onClose={() => setShowEventModal(false)}
        onSave={handleSaveEvent}
        eventToEdit={eventToEdit}
        members={members}
      />
    </div>
  );
};

export default Events;